import {
  buildSchedulerBootstrapScopeKey,
  isSchedulerBootstrapPayload,
  type SchedulerBootstrapPayload,
  type SchedulerBootstrapScope,
} from '../scheduler/bootstrap.ts';

const DB_NAME = 'mintleaf-scheduler';
const DB_VERSION = 2;
const STORE_NAME = 'bootstrap-snapshots';
const USER_ID_INDEX = 'by-user-id';
const SNAPSHOT_HARD_TTL_MS = 24 * 60 * 60 * 1000;

export type SchedulerSnapshotRecord = {
  scopeKey: string;
  userId: string;
  accessProfileVersion: number;
  permissionVersion: string;
  scheduleRevision: string;
  etag: string;
  savedAt: number;
  payload: SchedulerBootstrapPayload;
};

const memoryFallback = new Map<string, SchedulerSnapshotRecord>();
let databasePromise: Promise<IDBDatabase | null> | null = null;

const cloneRecord = (record: SchedulerSnapshotRecord): SchedulerSnapshotRecord =>
  typeof structuredClone === 'function'
    ? structuredClone(record)
    : JSON.parse(JSON.stringify(record)) as SchedulerSnapshotRecord;

const openDatabase = (): Promise<IDBDatabase | null> => {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      const store = database.objectStoreNames.contains(STORE_NAME)
        ? request.transaction!.objectStore(STORE_NAME)
        : database.createObjectStore(STORE_NAME, { keyPath: 'scopeKey' });
      if (!store.indexNames.contains(USER_ID_INDEX)) {
        store.createIndex(USER_ID_INDEX, 'userId', { unique: false });
      }
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => {
        database.close();
        databasePromise = null;
      };
      resolve(database);
    };
    request.onerror = () => {
      databasePromise = null;
      reject(request.error || new Error('Failed to open scheduler snapshot database.'));
    };
    request.onblocked = () => {
      databasePromise = null;
      resolve(null);
    };
  });

  return databasePromise;
};

const readIndexedDbRecord = async (scopeKey: string): Promise<SchedulerSnapshotRecord | null> => {
  const database = await openDatabase().catch(() => null);
  if (!database) return memoryFallback.get(scopeKey) ? cloneRecord(memoryFallback.get(scopeKey)!) : null;

  return new Promise<SchedulerSnapshotRecord | null>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const request = transaction.objectStore(STORE_NAME).get(scopeKey);
    request.onsuccess = () => resolve((request.result as SchedulerSnapshotRecord | undefined) ?? null);
    request.onerror = () => reject(request.error || new Error('Failed to read scheduler snapshot.'));
  });
};

const writeIndexedDbRecord = async (record: SchedulerSnapshotRecord): Promise<void> => {
  const database = await openDatabase().catch(() => null);
  if (!database) {
    memoryFallback.set(record.scopeKey, cloneRecord(record));
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(record);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('Failed to write scheduler snapshot.'));
    transaction.onabort = () => reject(transaction.error || new Error('Scheduler snapshot write was aborted.'));
  });
};

const deleteIndexedDbRecord = async (scopeKey: string): Promise<void> => {
  memoryFallback.delete(scopeKey);
  const database = await openDatabase().catch(() => null);
  if (!database) return;

  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(scopeKey);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('Failed to delete scheduler snapshot.'));
  });
};

export const getSchedulerSnapshot = async ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): Promise<SchedulerSnapshotRecord | null> => {
  if (typeof expectedAccessProfileVersion !== 'number' || !Number.isFinite(expectedAccessProfileVersion)) {
    return null;
  }

  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const record = await readIndexedDbRecord(scopeKey).catch(() => null);
  if (!record) return null;

  const isValid =
    record.scopeKey === scopeKey &&
    record.userId === scope.userId &&
    record.accessProfileVersion === expectedAccessProfileVersion &&
    typeof record.savedAt === 'number' &&
    now - record.savedAt <= SNAPSHOT_HARD_TTL_MS &&
    typeof record.etag === 'string' &&
    record.etag.length > 0 &&
    isSchedulerBootstrapPayload(record.payload) &&
    record.payload.scopeKey === scopeKey &&
    record.payload.accessProfileVersion === expectedAccessProfileVersion &&
    record.payload.permissions.canRead;

  if (!isValid) {
    await deleteIndexedDbRecord(scopeKey).catch(() => undefined);
    return null;
  }

  return cloneRecord(record);
};

export const setSchedulerSnapshot = async (
  payload: SchedulerBootstrapPayload,
  etag: string,
  now = Date.now()
): Promise<void> => {
  if (!payload.permissions.canRead || !etag) return;

  const record: SchedulerSnapshotRecord = {
    scopeKey: payload.scopeKey,
    userId: payload.userId,
    accessProfileVersion: payload.accessProfileVersion,
    permissionVersion: payload.permissionVersion,
    scheduleRevision: payload.scheduleRevision,
    etag,
    savedAt: now,
    payload,
  };
  await writeIndexedDbRecord(record);
};

export const deleteSchedulerSnapshot = async (scope: SchedulerBootstrapScope): Promise<void> =>
  deleteIndexedDbRecord(buildSchedulerBootstrapScopeKey(scope));


export const deleteSchedulerSnapshotsForUser = async (userId: string): Promise<void> => {
  const normalizedUserId = userId.trim();
  if (!normalizedUserId) return;

  for (const [scopeKey, record] of memoryFallback.entries()) {
    if (record.userId === normalizedUserId) memoryFallback.delete(scopeKey);
  }

  const database = await openDatabase().catch(() => null);
  if (!database || typeof IDBKeyRange === 'undefined') return;

  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const cursorRequest = transaction
      .objectStore(STORE_NAME)
      .index(USER_ID_INDEX)
      .openKeyCursor(IDBKeyRange.only(normalizedUserId));
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) return;
      transaction.objectStore(STORE_NAME).delete(cursor.primaryKey);
      cursor.continue();
    };
    cursorRequest.onerror = () => reject(cursorRequest.error || new Error('Failed to enumerate scheduler snapshots.'));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('Failed to clear scheduler snapshots.'));
    transaction.onabort = () => reject(transaction.error || new Error('Scheduler snapshot clear was aborted.'));
  });
};

export const __schedulerSnapshotHardTtlMs = SNAPSHOT_HARD_TTL_MS;
export const __resetSchedulerSnapshotMemoryForTests = (): void => memoryFallback.clear();
